import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar, MobileNav } from "@/components/Navbar";
import { api } from "@/lib/api";
import { CompBadge } from "@/components/OpportunityCard";
import { Button } from "@/components/ui/button";
import {
  Loader2, Plus, Users, Pencil, Copy, Eye, UserPlus, Megaphone,
  Inbox, CheckCircle2, Send, Calendar, MapPin, Archive, FolderKanban,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  draft: "bg-zinc-200 text-zinc-700",
  published: "bg-emerald-100 text-emerald-700",
  closed: "bg-rose-100 text-rose-700",
};

const TABS = [
  { key: "all", label: "All" },
  { key: "published", label: "Active" },
  { key: "events", label: "Events" },
  { key: "draft", label: "Drafts" },
  { key: "closed", label: "Closed" },
];

export default function BusinessOpportunities() {
  const navigate = useNavigate();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  const load = () => api.get("/opportunities/mine").then((r) => setOpps(r.data.opportunities)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const setStatus = async (o, status) => {
    await api.patch(`/opportunities/${o.id}/status`, { status });
    toast.success(status === "closed" ? "Opportunity closed" : "Opportunity published");
    load();
  };
  const duplicate = async (o) => {
    await api.post(`/opportunities/${o.id}/duplicate`);
    toast.success("Duplicated as a draft");
    load();
  };
  const cancelEvent = async (o) => {
    if (!window.confirm(`Cancel "${o.title}"? Applicants will be notified.`)) return;
    await api.post(`/opportunities/${o.id}/cancel`);
    toast.success("Event cancelled");
    load();
  };

  const sum = (key) => opps.reduce((a, o) => a + (o[key] || 0), 0);
  const stats = [
    { label: "Active", value: opps.filter((o) => o.status === "published").length, Icon: Megaphone, tint: "text-emerald-600 bg-emerald-100" },
    { label: "Drafts", value: opps.filter((o) => o.status === "draft").length, Icon: Pencil, tint: "text-zinc-600 bg-zinc-200" },
    { label: "Closed", value: opps.filter((o) => o.status === "closed").length, Icon: Archive, tint: "text-rose-600 bg-rose-100" },
    { label: "Applications", value: sum("applicant_count"), Icon: Inbox, tint: "text-amber-600 bg-amber-100" },
    { label: "Invited", value: sum("invited_count"), Icon: Send, tint: "text-indigo-600 bg-indigo-100" },
    { label: "Selected", value: sum("accepted_count"), Icon: CheckCircle2, tint: "text-emerald-600 bg-emerald-100" },
  ];

  const filtered = tab === "all" ? opps : tab === "events" ? opps.filter((o) => o.opp_type === "event") : opps.filter((o) => o.status === tab);

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-nav">
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Opportunities</h1>
            <p className="mt-1 text-zinc-500">Create campaigns, review applicants and invite creators.</p>
          </div>
          <Button data-testid="create-opportunity-btn" onClick={() => navigate("/business/opportunities/create")} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
            <Plus className="mr-1 h-4 w-4" /> Create Opportunity
          </Button>
        </div>

        <div className="mb-6">
          <Button data-testid="campaigns-link-btn" variant="outline" onClick={() => navigate("/business/campaigns")} className="rounded-full">
            <FolderKanban className="mr-1 h-4 w-4" /> Manage campaigns
          </Button>
        </div>

        {/* Stats */}
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {stats.map((s) => (
            <div key={s.label} data-testid={`opp-stat-${s.label.toLowerCase()}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
              <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${s.tint}`}><s.Icon className="h-4 w-4" /></div>
              <p className="mt-2 font-display text-2xl font-bold text-zinc-900">{s.value}</p>
              <p className="text-xs text-zinc-500">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="mb-5 flex gap-2 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`opp-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === t.key ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 border border-zinc-200"}`}>{t.label}</button>
          ))}
        </div>

        {loading ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
          : filtered.length === 0 ? (
            <div data-testid="biz-opp-empty" className="flex h-56 flex-col items-center justify-center rounded-2xl border border-dashed border-zinc-300 text-center">
              <Megaphone className="h-8 w-8 text-zinc-300" />
              <p className="mt-2 font-display text-lg font-semibold text-zinc-700">No opportunities here yet</p>
              <p className="mt-1 text-sm text-zinc-500">Create your first campaign to start finding creators.</p>
              <Button data-testid="create-opportunity-empty" onClick={() => navigate("/business/opportunities/create")} className="mt-4 rounded-full bg-emerald-600 hover:bg-emerald-700"><Plus className="mr-1 h-4 w-4" /> Create Opportunity</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((o) => (
                <div key={o.id} data-testid={`biz-opp-${o.id}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate font-display font-semibold text-zinc-900">{o.title || "Untitled"}</p>
                        {o.opp_type === "event" && <span className="rounded-full bg-fuchsia-600 px-2 py-0.5 text-xs font-bold text-white">EVENT</span>}
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLE[o.status]}`}>{o.status === "published" ? "Active" : o.status}</span>
                        {o.approval_state === "awaiting_business_approval" && <span data-testid={`awaiting-approval-${o.id}`} className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">Awaiting your approval</span>}
                        {o.opp_type === "event" && o.event_status && <span data-testid={`event-status-${o.id}`} className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-semibold capitalize text-zinc-600">{o.event_status.replace(/_/g, " ")}</span>}
                        <CompBadge type={o.compensation_type} />
                        {o.visibility === "direct" && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-600">Invite only</span>}
                      </div>
                      <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-zinc-500">
                        <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {o.location || "—"}</span>
                        <span>{o.category || "—"}</span>
                        <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {o.creators_needed} needed</span>
                        {o.deadline && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {o.deadline}</span>}
                      </p>
                    </div>
                  </div>

                  {/* metrics row */}
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-zinc-100 pt-3 text-xs text-zinc-500">
                    <span data-testid={`opp-metric-views-${o.id}`}><Eye className="mr-1 inline h-3.5 w-3.5" />{o.views} views</span>
                    <span><Inbox className="mr-1 inline h-3.5 w-3.5" />{o.applicant_count} applicants</span>
                    <span><Send className="mr-1 inline h-3.5 w-3.5" />{o.invited_count} invited</span>
                    <span><CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />{o.accepted_count} selected</span>
                    <span className="font-medium text-emerald-700">{o.places_available} place{o.places_available !== 1 ? "s" : ""} left</span>
                    {o.opp_type === "event" && o.enable_waitlist && <span className="font-medium text-amber-600">{o.waitlist_count} waitlisted</span>}
                  </div>

                  {o.opp_type === "event" && o.event_status === "completed" && (
                    <div data-testid={`post-event-prompt-${o.id}`} className="mt-3 rounded-xl border border-fuchsia-200 bg-fuchsia-50 p-3">
                      <p className="text-sm font-semibold text-zinc-800">How did your event go?</p>
                      <p className="text-xs text-zinc-600">Mark which creators attended so you can build your event history.</p>
                      <Button size="sm" data-testid={`review-event-${o.id}`} onClick={() => navigate(`/business/opportunities/${o.id}`)} className="mt-2 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700">Review attendees</Button>
                    </div>
                  )}

                  {/* actions */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {o.approval_state === "awaiting_business_approval" ? (
                      <>
                        <Button size="sm" data-testid={`review-approve-${o.id}`} onClick={() => navigate(`/business/opportunities/${o.id}/review`)} className="rounded-full bg-amber-500 hover:bg-amber-600"><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Review &amp; approve</Button>
                        <Button size="sm" variant="outline" data-testid={`edit-opp-${o.id}`} onClick={() => navigate(`/business/opportunities/${o.id}/edit`)} className="rounded-full"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                      </>
                    ) : (
                    <>
                    <Button size="sm" data-testid={`manage-opp-${o.id}`} onClick={() => navigate(`/business/opportunities/${o.id}`)} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><Users className="mr-1 h-3.5 w-3.5" /> Manage applicants</Button>
                    {o.visibility !== "public" && o.status !== "closed" && (
                      <Button size="sm" variant="outline" data-testid={`invite-opp-${o.id}`} onClick={() => navigate(`/discover?invite=${o.id}`)} className="rounded-full"><UserPlus className="mr-1 h-3.5 w-3.5" /> Invite creators</Button>
                    )}
                    <Button size="sm" variant="outline" data-testid={`edit-opp-${o.id}`} onClick={() => navigate(`/business/opportunities/${o.id}/edit`)} className="rounded-full"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                    <Button size="sm" variant="outline" data-testid={`duplicate-opp-${o.id}`} onClick={() => duplicate(o)} className="rounded-full"><Copy className="mr-1 h-3.5 w-3.5" /> Duplicate</Button>
                    {o.status !== "published" && <Button size="sm" variant="outline" data-testid={`publish-opp-${o.id}`} onClick={() => setStatus(o, "published")} className="rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50">Publish</Button>}
                    {o.status === "published" && <Button size="sm" variant="outline" data-testid={`close-opp-${o.id}`} onClick={() => setStatus(o, "closed")} className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50">Close</Button>}
                    {o.opp_type === "event" && !o.cancelled_at && o.event_status !== "completed" && <Button size="sm" variant="outline" data-testid={`cancel-event-${o.id}`} onClick={() => cancelEvent(o)} className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50">Cancel event</Button>}
                    </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
      <MobileNav />
    </div>
  );
}
